import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Users, ThumbsUp, ThumbsDown, ChevronRight } from 'lucide-react';
import './DiscussionSlide.css';

function DiscussionSlide({ showNotes }) {
  const [activeQuestion, setActiveQuestion] = useState(0);
  const [showPoll, setShowPoll] = useState(false);

  const questions = [
    {
      id: 1,
      question: 'Wo endet Meinungsfreiheit und wo beginnt Hetze?',
      context: 'Paludan darf den Koran verbrennen, weil es legal ist. Aber er tut es, um Gewalt zu provozieren. Sollte das erlaubt sein?',
      proArgument: 'Meinungsfreiheit muss auch unangenehme Meinungen schützen',
      contraArgument: 'Gezielte Provokation zum Schaden ist keine schützenswerte Meinung',
      icon: '🗣️'
    },
    {
      id: 2,
      question: 'Sollte die EU härter gegen Mitgliedsstaaten vorgehen, die demokratische Normen verletzen?',
      context: 'Ungarn und Polen erhielten jahrelang EU-Gelder trotz Demokratieabbau.',
      proArgument: 'EU-Werte müssen durchgesetzt werden',
      contraArgument: 'Einmischung in nationale Souveränität',
      icon: '🇪🇺'
    },
    {
      id: 3,
      question: 'Wie können junge Menschen gegen Online-Radikalisierung geschützt werden, ohne das Internet zu zensieren?',
      context: 'Social Media und Algorithmen fördern extreme Inhalte.',
      proArgument: 'Medienkompetenz und Bildung statt Verbote',
      contraArgument: 'Plattformen müssen mehr Verantwortung übernehmen',
      icon: '📱'
    },
  ];

  const currentQ = questions[activeQuestion];

  return (
    <div className="slide discussion-slide">
      <div className="discussion-layout">
        {/* Header */}
        <div className="discussion-header">
          <motion.span
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="section-label"
          >
            Interaktiv
          </motion.span>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="slide-title"
          >
            Diskussion
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="slide-subtitle"
          >
            Eure Meinung ist gefragt!
          </motion.p>
        </div>

        <div className="discussion-content">
          {/* Question Selector */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="question-selector"
          >
            {questions.map((q, index) => (
              <button
                key={q.id}
                className={`question-tab ${activeQuestion === index ? 'active' : ''}`}
                onClick={() => setActiveQuestion(index)}
              >
                <span className="tab-icon">{q.icon}</span>
                <span className="tab-number">Frage {q.id}</span>
              </button>
            ))}
          </motion.div>

          {/* Main Question Area */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeQuestion}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="question-area"
            >
              {/* Question Card */}
              <div className="question-card">
                <div className="question-header">
                  <span className="question-icon">{currentQ.icon}</span>
                  <span className="question-badge">Frage {currentQ.id}</span>
                </div>
                
                <h3 className="question-text">{currentQ.question}</h3>
                
                <div className="question-context">
                  <span className="context-label">Kontext:</span>
                  <p>{currentQ.context}</p>
                </div>
              </div>

              {/* Arguments Grid */}
              <div className="arguments-grid">
                <div className="argument-card pro">
                  <div className="argument-header">
                    <ThumbsUp size={18} />
                    <span>Pro</span>
                  </div>
                  <p>{currentQ.proArgument}</p>
                </div>
                
                <div className="argument-card contra">
                  <div className="argument-header">
                    <ThumbsDown size={18} />
                    <span>Contra</span>
                  </div>
                  <p>{currentQ.contraArgument}</p>
                </div>
              </div>

              {/* Quick Poll */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="poll-section"
              >
                <button 
                  className="poll-toggle"
                  onClick={() => setShowPoll(!showPoll)}
                >
                  <Users size={18} />
                  <span>Schnelle Abstimmung</span>
                  <ChevronRight 
                    size={18} 
                    className={`chevron ${showPoll ? 'open' : ''}`}
                  />
                </button>
                
                <AnimatePresence>
                  {showPoll && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="poll-content"
                    >
                      <p className="poll-instruction">Hebt die Hand:</p>
                      <div className="poll-options">
                        <div className="poll-option">
                          <span className="poll-emoji">👍</span>
                          <span>Stimme zu</span>
                        </div>
                        <div className="poll-option">
                          <span className="poll-emoji">🤔</span>
                          <span>Unentschieden</span>
                        </div>
                        <div className="poll-option">
                          <span className="poll-emoji">👎</span>
                          <span>Stimme nicht zu</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </motion.div>
          </AnimatePresence>

          

        </div>
      </div>

      {showNotes && (
        <div className="speaker-notes">
          <strong>📚 Zusatzinfos für Leser:</strong><br/>
          <strong>Zu Frage 1 – Meinungsfreiheit vs. Hetze:</strong><br/>
          In Deutschland schützt Art. 5 GG die Meinungsfreiheit, aber §130 StGB (Volksverhetzung) 
          setzt Grenzen. In Schweden ist der Schutz der Meinungsfreiheit weiter gefasst.<br/><br/>
          <strong>Zu Frage 2 – EU und Rechtsstaatlichkeit:</strong><br/>
          Art. 7 EUV ermöglicht Sanktionen gegen Mitgliedsstaaten, wurde aber bisher nie 
          vollständig angewendet. Der neue Rechtsstaatsmechanismus (2021) verknüpft EU-Gelder 
          mit Rechtsstaatlichkeit.<br/><br/>
          <strong>Zu Frage 3 – Online-Radikalisierung:</strong><br/>
          Studien zeigen: Algorithmen bevorzugen emotionale, oft extreme Inhalte. 
          Gegenmaßnahmen: Medienkompetenz in Schulen, Plattformregulierung (DSA), 
          Förderung von "Counter-Speech".<br/><br/>
          <strong>Zum Nachdenken:</strong> Diese Fragen haben keine einfachen Antworten. 
          Sie erfordern eine Abwägung zwischen verschiedenen demokratischen Grundwerten.
        </div>
      )}
    </div>
  );
}

export default DiscussionSlide;
